import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Per-page SEO tags. Pass minimum `title` and `description`; the rest fall back
 * to sensible site-wide defaults. For product/article pages, also pass `image`,
 * `type` ("product" | "article" | "website"), `price`, `availability`, and
 * `structuredData` (a JS object that we'll JSON-encode into a ld+json script).
 *
 *   <SEO title="Charizard ex" description="..." image="..." type="product"
 *        price={99.99} structuredData={{ "@context":"https://schema.org", "@type":"Product", ... }} />
 *
 * Reads canonical URL from window.location at render time so links produced by
 * client-side routing stay accurate. Server-side rendering would need to thread
 * the URL in via a prop — out of scope for now.
 */
const SITE = {
  name: 'SilverGames',
  description: 'Canadian-owned Pokemon TCG shop — singles, sealed, graded slabs, daily restocks.',
  defaultImage: '/og-default.png',
  url: 'https://silvergames.ca',
};

// Cache the public site_settings once per session and pull twitter handle
// from there so admin can rebrand without a code change. Falls back to
// '@silvergames' when the setting is missing or fetch fails.
let _twitterHandleCache = null;
async function _resolveTwitterHandle() {
  if (_twitterHandleCache !== null) return _twitterHandleCache;
  try {
    const r = await fetch('/api/site/settings/public');
    const map = r.ok ? await r.json() : {};
    const raw = (map['social.twitter'] || map['social.x'] || '').trim();
    // Accept either '@handle' or a full URL — extract the handle either way.
    let handle = raw;
    const urlMatch = raw.match(/(?:twitter\.com|x\.com)\/@?([A-Za-z0-9_]+)/i);
    if (urlMatch) handle = '@' + urlMatch[1];
    else if (handle && !handle.startsWith('@')) handle = '@' + handle;
    _twitterHandleCache = handle || '@silvergames';
  } catch (_) {
    _twitterHandleCache = '@silvergames';
  }
  return _twitterHandleCache;
}

export default function SEO({
  title,
  description,
  image,
  type = 'website',
  noindex = false,
  canonical,
  structuredData,
  price,
  priceCurrency = 'CAD',
  availability,
}) {
  const fullTitle = title ? `${title} · ${SITE.name}` : SITE.name;
  const desc = description || SITE.description;
  const ogImage = image || SITE.defaultImage;
  const url = canonical || (typeof window !== 'undefined' ? window.location.href : SITE.url);

  // Resolve twitter handle once; until it lands, render the default — Helmet
  // will swap the meta tag in place when the state updates.
  const [twitterHandle, setTwitterHandle] = useState(_twitterHandleCache || '@silvergames');
  useEffect(() => { _resolveTwitterHandle().then(setTwitterHandle); }, []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="en-ca" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />
      <meta name="geo.region" content="CA-ON" />
      <meta name="geo.placename" content="Ontario, Canada" />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={absoluteUrl(ogImage)} />
      {price != null && <meta property="product:price:amount" content={String(price)} />}
      {price != null && <meta property="product:price:currency" content={priceCurrency} />}
      {availability && <meta property="product:availability" content={availability} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={absoluteUrl(ogImage)} />

      {/* JSON-LD structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

function absoluteUrl(path) {
  if (!path) return SITE.defaultImage;
  if (/^https?:\/\//.test(path)) return path;
  if (typeof window !== 'undefined') return new URL(path, window.location.origin).toString();
  return SITE.url + (path.startsWith('/') ? path : `/${path}`);
}
